/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service for managing user ride data. Handles ride history tracking,
booking management, and ride-related statistics calculations.
*/

import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { BikeRide } from "./types";

// User rides service
export const userRidesService = {
  // Get user ride history - FIXED to handle bike_id from bookings and include count_towards_offset
  getUserRides: async (userId: string, page: number = 1, pageSize: number = 10): Promise<{ rides: BikeRide[]; total: number }> => {
    try {
      console.log(`Fetching rides for user ${userId}: page ${page}, size ${pageSize}`);
      
      // Calculate range for pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Fetch rides from Supabase - updated to join with bookings to get bike_id and bike details
      const { data, error, count } = await supabase
        .from('rides')
        .select(`
          *,
          booking:bookings(
            bike_id,
            start_location_type,
            start_location_id,
            end_location_type,
            end_location_id,
            start_site:sites!bookings_start_location_id_fkey(name),
            end_site:sites!bookings_end_location_id_fkey(name),
            bikes!inner(
              serial_number,
              model:bike_models(name)
            )
          )
        `, { count: 'exact' })
        .eq('user_id', userId)
        .range(from, to)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Map to BikeRide interface
      const rides: BikeRide[] = data ? data.map((ride): BikeRide => {
        const startTime = ride.actual_start_time ? new Date(ride.actual_start_time) : new Date(ride.created_at);
        let endTime: Date | null = null;
        let durationMinutes: number | null = null;
        if (ride.actual_end_time) {
          endTime = new Date(ride.actual_end_time);
          durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        } else {
          endTime = null;
          durationMinutes = null;
        }
        
        // Get bike details from booking relation
        const bikeIdFromBooking = ride.booking?.bike_id;
        const serialNumber = ride.booking?.bikes?.serial_number;
        const bikeName = ride.booking?.bikes?.model?.name;
        
        // Get location details
        const startLocation = ride.booking?.start_location_type === 'site' 
          ? ride.booking?.start_site?.name || 'Unknown Site'
          : 'Home';
        const endLocation = ride.booking?.end_location_type === 'site'
          ? ride.booking?.end_site?.name || 'Unknown Site'
          : 'Home';
        
        return {
          id: ride.id,
          bikeId: bikeIdFromBooking || 'unknown',
          serialNumber: serialNumber || null,
          bikeName: bikeName || null,
          userId: ride.user_id,
          userName: "From Booking", // Would need to join with users table
          startTime,
          endTime,
          startLocation,
          endLocation,
          distance: ride.distance_traveled_km || 0,
          duration: durationMinutes,
          countTowardsOffset: ride.count_towards_offset !== false, // Default to true if not specified
        };
      }) : [];
      
      console.log(`Successfully fetched ${rides.length} rides for user ${userId}`);
      return { rides, total: count || 0 };
    } catch (error) {
      console.error(`Error fetching rides for user ${userId}:`, error);
      toast.error("Failed to load ride history. Please try again.");
      throw error;
    }
  },
};
